import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch, buildParams } from "@/lib/api";
import type {
  PaginatedProjectListOutput,
  ProjectFavoriteToggleOutput,
  ProjectListParams,
} from "@/types";

export function useFavoriteProject(
  accountId: string,
  listParams: ProjectListParams,
  projectCode: string,
  role: string
) {
  const queryClient = useQueryClient();
  const listKey = ["project-list", listParams];
  const favoritesKey = ["project-favorites", listParams];

  return useMutation({
    mutationFn: () =>
      apiFetch<ProjectFavoriteToggleOutput>(
        `/v1/home/project-favorite/${projectCode}${buildParams({ account_id: accountId, role: role })}`,
        { method: "PUT" }
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: favoritesKey });

      const previous = queryClient.getQueryData<PaginatedProjectListOutput>(listKey);
      const previousFavorites = queryClient.getQueryData<PaginatedProjectListOutput>(favoritesKey);

      queryClient.setQueryData<PaginatedProjectListOutput>(listKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.project_code === projectCode ? { ...item, is_favorite: !item.is_favorite } : item
          ),
        };
      });

      queryClient.setQueryData<PaginatedProjectListOutput>(favoritesKey, (old) => {
        if (!old) return old;
        const target = old.items.find((item) => item.project_code === projectCode);
        if (!target) {
          // Item not in the favorites page yet (e.g. toggled from the All tab).
          // Find it in the pre-update project-list snapshot and inject it as a favorite.
          const fromList = previous?.items.find((item) => item.project_code === projectCode);
          if (!fromList || fromList.is_favorite) return old;
          return {
            ...old,
            items: [...old.items, { ...fromList, is_favorite: true }],
            total: old.total + 1,
          };
        }
        // Items in the favorites cache always have is_favorite === true; reaching here means un-favoriting.
        return {
          ...old,
          items: old.items.filter((item) => item.project_code !== projectCode),
          total: old.total - 1,
        };
      });

      return { previous, previousFavorites };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(listKey, context?.previous);
      queryClient.setQueryData(favoritesKey, context?.previousFavorites);
    },
    onSettled: () => {
      // Both prefixes are intentionally broad: invalidate all project-list and
      // project-favorites queries regardless of pagination params, so toggling
      // from either tab always causes both tabs to refetch.
      queryClient.invalidateQueries({ queryKey: ["project-list"] });
      queryClient.invalidateQueries({ queryKey: ["project-favorites"] });
      queryClient.invalidateQueries({ queryKey: ["project-status"] });
      queryClient.invalidateQueries({ queryKey: ["project-deadline"] });
    },
  });
}
